import React, { useState } from 'react';
import { map, find, defaultTo, noop, head, castArray, isEmpty, isNil } from 'lodash';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { getLabel } from 'utils/getLabel';
import { GLOBAL_LABELS } from 'constants/common';
import { button as Button } from 'modules/button';
import { Icon } from 'modules/icon-bank';
import { select as Select } from 'ui/views/generic/select';

// readonly view
const CallDispositionReasonsReadonly = ( props ) => {
    
    // extract actions
    const addAction = find( props.actions, { name: 'add' } );

    // const cancelction = find( props.actions, { name: 'cancel' } );
    // const saveAction = find( props.actions, { name: 'save' } );
    // const deleteAction = find( props.actions, { name: 'delete' } );

    return (
        <div className='ui-call-disposition-reasons__readonly'>

            {/* add button */}
            <div className='ui-call-disposition-reasons__readonly__add-button'>
                <Button
                    className='ui-call-disposition-reasons__readonly__add-button__button'
                    title={ addAction.title }
                    size='extra-small'
                    onClick={ () => props.onToggleEditMode( true ) }
                />
            </div>

            {/* item */}
            <div className='ui-call-disposition-reasons__readonly__items'>
                {
                    map( props.items, item => {

                        // get options
                        const categoryOption = defaultTo( find( props.options, { value: item.category } ), {} );
                        const reasonOption = defaultTo( find( categoryOption.options, { value: item.reason } ), {} );

                        return (
                            <div key={ item.id } className='ui-call-disposition-reasons__readonly__items__item'>
                                <div className='ui-call-disposition-reasons__readonly__items__item__text'>
                                    { getLabel( categoryOption.title ) } { getLabel( GLOBAL_LABELS.DISPLAY_TEXT_AND_LOWER_CASE ) } { getLabel( reasonOption.title ) }
                                </div>
                                <div className='ui-call-disposition-reasons__readonly__items__item__actions'>
                                    <div className='ui-call-disposition-reasons__readonly__items__item__actions__edit' onClick={ () => props.onToggleEditMode( true, item.id ) }>
                                        <Icon name='edit' />
                                    </div>
                                    <div className='ui-call-disposition-reasons__readonly__items__item__actions__delete' onClick={ () => props.onDeleteClick( { id: item.id } ) }>
                                        <Icon name='delete' />
                                    </div>
                                </div>
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// editable view
const CallDispositionReasonsEditable = ( props ) => {
    
    // extract actions
    const cancelction = find( props.actions, { name: 'cancel' } );
    const saveAction = find( props.actions, { name: 'save' } );

    // find currently editing item based on `props.editItemId`
    const item = isNil( props.editItemId ) ? null : find( props.items, { id: props.editItemId } );
    const itemCategoryOption = isEmpty( item ) ? undefined : find( props.options, { value: item.category } );
    const itemReasonOption = isEmpty( itemCategoryOption ) ? undefined : find( itemCategoryOption.options, { value: item.reason } );

    // selected category and reason option
    const [ category, setCategory ] = useState( itemCategoryOption );
    const [ reason, setReason ] = useState( itemReasonOption );

    return (
        <div className='ui-call-disposition-reasons__editable'>

            {/* category */}
            <div className='ui-call-disposition-reasons__editable__field'>
                <div className='ui-call-disposition-reasons__editable__field__title'>
                    { getLabel( 'displayTextCategory' ) }
                </div>
                <div className='ui-call-disposition-reasons__editable__field__input'>
                    <Select
                        placeholder={ getLabel( 'displayTextCategory' ) }
                        options={ props.options }
                        value={ isEmpty( category ) ? '' : category.value }
                        onChange={ ( value ) => {
                            setCategory( find( props.options, { value: head( castArray( value ) ) } ) );
                            setReason( undefined );
                        } }
                    />
                </div>
            </div>

            {/* reason */}
            <div className='ui-call-disposition-reasons__editable__field'>
                <div className='ui-call-disposition-reasons__editable__field__title'>
                    { getLabel( 'displayTextReason' ) }
                </div>
                <div className='ui-call-disposition-reasons__editable__field__input'>
                    <Select
                        placeholder={ getLabel( 'displayTextReason' ) }
                        options={ isEmpty( category ) ? [] : category.options }
                        value={ isEmpty( reason ) ? '' : reason.value }
                        disabled={ isEmpty( category ) }
                        onChange={ ( value ) => {
                            setReason( find( category.options, { value: head( castArray( value ) ) } ) );
                        } }
                    />
                </div>
            </div>

            {/* buttons */}
            <div className='ui-call-disposition-reasons__editable__buttons'>
                <Button
                    className='ui-call-disposition-reasons__editable__buttons__item'
                    size='extra-small'
                    outline={ true }
                    title={ cancelction.title }
                    onClick={ () => props.onToggleEditMode( false ) }
                />

                <Button
                    className='ui-call-disposition-reasons__editable__buttons__item'
                    size='extra-small'
                    title={ saveAction.title }
                    disabled={ isEmpty( category ) || isEmpty( reason ) }
                    onClick={ () => props.onFormSubmit( { id: props.editItemId, category, reason } ) }
                />
            </div>
        </div>
    );
};

// success view
export const callDispositionReasonsSuccess = ( props ) => {
    return (
        <div className='ui-call-disposition-reasons'>
            {
                'readonly' === props.mode ? (
                    <CallDispositionReasonsReadonly { ...props } />
                ) : (
                    <CallDispositionReasonsEditable { ...props } />
                )
            }
        </div>
    );
};

// set display name
callDispositionReasonsSuccess.displayName = 'callDispositionReasonsSuccess';

// set default props
callDispositionReasonsSuccess.defaultProps = {
    mode: 'readonly',
    editItemId: null,
    items: [],
    options: [],
    actions: [],
    onToggleEditMode: noop,
    onFormSubmit: noop,
    onDeleteClick: noop,
};

// prop types
callDispositionReasonsSuccess.propTypes = {};


/*******************************/

// preloader view
export const callDispositionReasonsPreloader = () => {
    return (
        <div className='ui-call-disposition-reasons-preloader'>
            <div className='ui-call-disposition-reasons-preloader__actions'>
                <div className='ui-call-disposition-reasons-preloader__actions__button' />
            </div>

            <div className='ui-call-disposition-reasons-preloader__items'>
                {
                    map( [ 1, 2 ], item => {
                        return (
                            <div key={ item } className='ui-call-disposition-reasons-preloader__items__item'>
                                <div className='ui-call-disposition-reasons-preloader__items__item__text' />
                                <div className='ui-call-disposition-reasons-preloader__items__item__actions' />
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
callDispositionReasonsPreloader.displayName = 'callDispositionReasonsPreloader';

// set default props
callDispositionReasonsPreloader.defaultProps = {};

// prop types
callDispositionReasonsPreloader.propTypes = {};


/*******************************/


/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { callDispositionReasons as CallDispositionReasons } from 'ui/views/widgets/callDispositionReasons';
 *
 * // render
 * <CallDispositionReasons propName='propValue'/>
 */
export const callDispositionReasons = withNetworkState( callDispositionReasonsPreloader, callDispositionReasonsSuccess );
